import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero, Section, Btn, Container } from "@/components/kit";
import { Eyebrow } from "@/lib/anim";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/rasika-khode/15min";

const STAGES = [
  "I haven't placed my first order",
  "Launched, under $1M",
  "$1M to $5M",
  "$5M to $20M",
  "$20M+",
];
const BUDGETS = [
  "Under $20k",
  "$20k to $50k",
  "$50k to $150k",
  "$150k to $500k",
  "$500k+",
  "Not sure yet",
];

function CalendlyEmbed() {
  const ref = useRef(null);
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (e) { /* script already removed */ } };
  }, []);
  return (
    <div>
      <div ref={ref} className="calendly-inline-widget" data-url={`${CALENDLY}?hide_gdpr_banner=1`} style={{ minWidth: 320, height: 720 }} data-testid="calendly-embed" />
      <p className="t-caption text-[var(--muted)] mt-4">
        If the scheduler does not load, <a href={CALENDLY} target="_blank" rel="noreferrer" className="lnk">pick a time here</a>. Times shown in your local timezone.
      </p>
    </div>
  );
}

export default function Book() {
  const [step, setStep] = useState(1);
  const [recommended, setRecommended] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", business: "", website: "", stage: "", problem: "", budget: "", newsletter: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.business.trim() || !f.stage || !f.budget || !f.problem.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fit-call`, f);
      setRecommended(data.recommended || "");
      track("fit_call_submitted", { stage: f.stage, budget: f.budget });
      setStep(2);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong. Please try again or email info@prox-au.com.");
    }
    setBusy(false);
  };

  const label = "t-caption font-mono uppercase tracking-wide text-[var(--muted)]";

  return (
    <>
      <Seo title="Book a fit call" canonical="/book" description="Book a free 20-minute fit call with Prox. A short qualification, then pick a time. A straight answer on whether Prox can help." />
      <PageHero
        eyebrow="Free fit call"
        crumbs={[{ label: "Home", to: "/" }, { label: "Book" }]}
        title={step === 1 ? "Book a 20-minute fit call." : "Now pick a time."}
        lead={step === 1
          ? "Twenty minutes, no charge, no pitch. Tell us the problem and we will tell you which piece of work fits, what it costs, or that Prox is not the right answer."
          : "Choose a slot below. If you have a costing sheet, a factory quote or a sales export, send them ahead."}
      />

      <Section bg="bone">
        <Container size="md" className="!px-0">
          {/* progress */}
          <div className="flex items-center gap-3 mb-10 font-mono text-[12px] uppercase tracking-wide">
            <span style={{ color: step >= 1 ? "var(--clay-deep)" : "var(--muted)" }}>01 A few questions</span>
            <span className="w-8 h-px bg-[var(--stone)]" />
            <span style={{ color: step >= 2 ? "var(--clay-deep)" : "var(--muted)" }}>02 Pick a time</span>
          </div>

          {step === 1 && (
            <p className="t-sm text-[var(--muted)] -mt-4 mb-8">This call is free. If you would rather go straight to a paid 60 or 90 minute strategy call, <Link to="/pricing" className="lnk">see pricing</Link>.</p>
          )}

          {step === 1 ? (
            <form onSubmit={submit} className="space-y-6" data-testid="fit-call-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <label className="block"><span className={label}>Your name *</span>
                  <input className="field mt-2" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="fc-name" /></label>
                <label className="block"><span className={label}>Email *</span>
                  <input type="email" className="field mt-2" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="fc-email" /></label>
                <label className="block"><span className={label}>Business name *</span>
                  <input className="field mt-2" value={f.business} onChange={(e) => set("business", e.target.value)} data-testid="fc-business" /></label>
                <label className="block"><span className={label}>Phone (optional)</span>
                  <input type="tel" className="field mt-2" placeholder="+61…" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="fc-phone" autoComplete="tel" /></label>
                <label className="block"><span className={label}>Website or Instagram</span>
                  <input className="field mt-2" value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="fc-website" /></label>
                <label className="block"><span className={label}>Business stage *</span>
                  <select className="field mt-2" value={f.stage} onChange={(e) => set("stage", e.target.value)} data-testid="fc-stage">
                    <option value="">Select</option>
                    {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select></label>
                <label className="block"><span className={label}>Next production run budget *</span>
                  <select className="field mt-2" value={f.budget} onChange={(e) => set("budget", e.target.value)} data-testid="fc-budget">
                    <option value="">Select</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select></label>
              </div>
              <label className="block"><span className={label}>What is the problem you want help with? *</span>
                <textarea maxLength={500} rows={4} className="field mt-2 resize-none" value={f.problem} onChange={(e) => set("problem", e.target.value)} data-testid="fc-problem" />
                <span className="block t-caption text-[var(--muted)] mt-1 text-right">{f.problem.length}/500</span>
              </label>
              <label className="flex items-start gap-3">
                <input type="checkbox" checked={f.newsletter} onChange={(e) => set("newsletter", e.target.checked)} className="w-4 h-4 mt-1 accent-[var(--clay)]" data-testid="fc-newsletter" />
                <span className="t-sm text-[var(--muted)]">I would like the fortnightly email on cost, margin and sourcing.</span>
              </label>
              <p className="t-caption text-[var(--muted)]">Your details are used to prepare for the call and nothing else. See the <a href="/privacy-policy" className="lnk">privacy policy</a>.</p>
              <Btn type="submit" variant="clay" data-testid="fc-submit">{busy ? "Just a moment\u2026" : "Continue to choose a time"}</Btn>
            </form>
          ) : (
            <div>
              {recommended && (
                <div className="card p-6 mb-8" data-testid="fit-call-recommendation">
                  <span className="eyebrow">Based on what you told me</span>
                  <p className="t-body mt-2">A good fit is likely <strong>{recommended}</strong>. We will confirm on the call, and there is never any pressure to take it.</p>
                </div>
              )}
              <CalendlyEmbed />
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
